import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ActionKind,
  ActionResponse,
  AuditResponse,
  BoardResponse,
  RosterImportRequest,
  Settings,
  SessionUser,
} from '@chemoflow/contracts';
import { api, newRequestId, post, put } from './client.js';

export const BOARD_KEY = ['board'] as const;
const AUDIT_KEY = ['audit'] as const;
const SESSION_KEY = ['session'] as const;

/** 状態取得の間隔。要件「数秒以内」に対する初期案で、正式な値は病院と合意する。 */
export const POLL_INTERVAL_MS = Number(import.meta.env.VITE_POLL_INTERVAL_MS ?? 3000);

export interface SessionState {
  user: SessionUser | null;
  authKind: 'mock' | 'entra';
}

export function useSession(): UseQueryResult<SessionState> {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: () => api<SessionState>('/api/auth/me'),
    staleTime: 30_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (role: SessionUser['role']) =>
      post<{ user: SessionUser }>('/api/auth/mock-login', { role }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: SESSION_KEY });
      await qc.invalidateQueries({ queryKey: BOARD_KEY });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ ok: boolean }>('/api/auth/logout'),
    onSuccess: () => {
      // ログアウト時に手元の患者表示情報を破棄する（実装プラン 5章）
      qc.clear();
    },
  });
}

/**
 * 業務状態の取得。
 * ・前景表示中は数秒間隔で取り直す
 * ・復帰・フォーカス時にも取り直し、古い結果で新しい状態を上書きしない
 * ・患者表示情報を含むため、画面を離れたらキャッシュを残さない（gcTime 0）
 */
export function useBoard(enabled: boolean): UseQueryResult<BoardResponse> {
  return useQuery({
    queryKey: BOARD_KEY,
    queryFn: () => api<BoardResponse>('/api/board'),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });
}

/** 応答で返った1件を、次のポーリングを待たずに画面へ反映する。 */
function patchCase(
  qc: ReturnType<typeof useQueryClient>,
  updated: ActionResponse['case'],
  serverTime: number,
) {
  qc.setQueryData<BoardResponse>(BOARD_KEY, (prev) =>
    prev
      ? {
          ...prev,
          serverTime,
          updatedAt: serverTime,
          cases: prev.cases.map((c) => (c.caseId === updated.caseId ? updated : c)),
        }
      : prev,
  );
}

export interface ActionInput {
  caseId: string;
  action: ActionKind;
  reason?: string;
  expectedVersion: number;
  /**
   * 再送の二重反映を防ぐID。
   * mutationFn の中で採ると再送のたびに別のIDになり、サーバ側の冪等性が働かない。
   * 呼び出し側が操作1回につき1つ採り、同じ要求の再送では同じIDを使う。
   */
  requestId: string;
}

export interface UndoInput {
  caseId: string;
  expectedVersion: number;
  requestId: string;
}

/**
 * 更新の前に、飛んでいる状態取得を打ち切る。
 *
 * query-core 5.x の Query#fetch は解決した取得結果を無条件に setData する。
 * 更新より前に出たポーリングが更新より後に返ると、確定した結果をその場で古い状態へ戻してしまう
 * （薬剤部の大型モニタでは最大1ポーリング分そのまま見える）。
 * cancelQueries は retryer を即 reject するので、遅れて返った取得は setData まで届かない。
 */
const cancelBoardFetch = (qc: ReturnType<typeof useQueryClient>) =>
  qc.cancelQueries({ queryKey: BOARD_KEY });

/** ステータス更新。成功はサーバが確定してから扱う（先行して画面だけ進めない）。 */
export function useCaseAction() {
  const qc = useQueryClient();
  return useMutation<ActionResponse, Error, ActionInput>({
    mutationFn: ({ caseId, action, reason, expectedVersion, requestId }) =>
      post<ActionResponse>(`/api/cases/${caseId}/actions`, {
        requestId,
        action,
        reason,
        expectedVersion,
      }),
    onMutate: () => cancelBoardFetch(qc),
    onSuccess: (data) => patchCase(qc, data.case, data.serverTime),
  });
}

export function useCaseUndo() {
  const qc = useQueryClient();
  return useMutation<ActionResponse, Error, UndoInput>({
    mutationFn: ({ caseId, expectedVersion, requestId }) =>
      post<ActionResponse>(`/api/cases/${caseId}/undo`, {
        requestId,
        expectedVersion,
      }),
    onMutate: () => cancelBoardFetch(qc),
    onSuccess: (data) => patchCase(qc, data.case, data.serverTime),
  });
}

export function useSettingsMutation() {
  const qc = useQueryClient();
  return useMutation<Settings, Error, Partial<Settings>>({
    mutationFn: (patch) => put<Settings>('/api/settings', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: BOARD_KEY }),
  });
}

/** 締切後の追加オーダーへ追随するための再取得（要件9-3）。 */
export function useRosterRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ added: number; removed: number; kept: number }>('/api/roster/refresh'),
    onSuccess: () => qc.invalidateQueries({ queryKey: BOARD_KEY }),
  });
}

export function useRosterImport() {
  const qc = useQueryClient();
  return useMutation<
    { added: number; removed: number; kept: number },
    Error,
    Omit<RosterImportRequest, 'requestId'>
  >({
    mutationFn: (input) => post('/api/roster/import', { ...input, requestId: newRequestId() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BOARD_KEY });
      await qc.invalidateQueries({ queryKey: AUDIT_KEY });
    },
  });
}

/** 操作ログ。氏名は保存されておらず、表示のたびに代理キーから解決されたものが返る。 */
export function useAudit(enabled: boolean): UseQueryResult<AuditResponse> {
  return useQuery({
    queryKey: AUDIT_KEY,
    queryFn: () => api<AuditResponse>('/api/audit?limit=200'),
    enabled,
    gcTime: 0,
    staleTime: 0,
  });
}

/** 検証用。本番では組み込まれない。 */
export function useDevControls() {
  const qc = useQueryClient();
  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: BOARD_KEY });
    await qc.invalidateQueries({ queryKey: AUDIT_KEY });
  };
  return {
    reset: useMutation({
      mutationFn: (mode: 'sample' | 'clear') => post('/api/dev/reset', { mode }),
      onSuccess: invalidate,
    }),
    setProviderUnavailable: useMutation({
      mutationFn: (unavailable: boolean) => post('/api/dev/patient-provider', { unavailable }),
      onSuccess: invalidate,
    }),
  };
}
